import { useEffect, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import clsx from 'clsx';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import * as R from 'ramda';

import { useNavigation } from '@atom/hooks/useNavigation';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkCollectionItem } from '@atom/types/work';
import api from '@atom/utilities/api';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import { getWorkOrderStatusCalendarColor } from '@atom/utilities/workOrderStatusUtilities';

import ScheduleCalendarEvent from './ScheduleCalendarEvent';
import ScheduleCalendarToolbar from './ScheduleCalendarToolbar';

import classes from './schedule.module.css';

const styles = {
  calendar: {
    height: 'calc(100vh - 60px)',
  },
  event: {
    color: colors.neutral.dim,
    fontSize: fonts.sm,
    borderRadius: '2px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
  },
};

interface CalendarFilters {
  calendarEnd: number;
  calendarStart: number;
}

interface WorkOrdersDictionary {
  [key: string]: WorkCollectionItem;
}

const getCalendarFilters = (view: string, viewDate: any): CalendarFilters => {
  switch (view) {
    case 'month': {
      return {
        calendarStart: startOfMonth(viewDate).valueOf(),
        calendarEnd: endOfMonth(viewDate).valueOf(),
      };
    }
    case 'week': {
      return {
        calendarStart: startOfWeek(viewDate).valueOf(),
        calendarEnd: endOfWeek(viewDate).valueOf(),
      };
    }
    case 'day': {
      return {
        calendarStart: startOfDay(viewDate).valueOf(),
        calendarEnd: endOfDay(viewDate).valueOf(),
      };
    }
    default: {
      return null;
    }
  }
};

// Needed for react-big-calendar
// https://github.com/jquense/react-big-calendar/blob/master/README.md
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const ScheduleCalendar = () => {
  const [, { navigate }] = useNavigation();
  const [loading, setLoading] = useState<boolean>(false);
  const [workOrdersDictionary, setWorkOrdersDictionary] =
    useState<WorkOrdersDictionary>({});

  const [viewDate, setViewDate] = useState<any>(new Date());
  const [view, setView] = useState<string>('month');

  const [calendarFilters, setCalendarFilters] = useState<CalendarFilters>(
    getCalendarFilters(view, viewDate),
  );

  useEffect(() => {
    setCalendarFilters(getCalendarFilters(view, viewDate));
  }, [view, viewDate]);

  useEffect(() => {
    const getWorkOrders = async () => {
      setLoading(true);
      const params = {
        page: 1,
        limit: 250,
        sortBy: 'name',
        isAscending: true,
        dueDateStart: calendarFilters.calendarStart,
        dueDateEnd: calendarFilters.calendarEnd,
      };

      const { data } = await api.get(WORK_ORDERS_ENDPOINT, params);

      const dictionary = data.reduce((acc, workOrder) => {
        return { ...acc, [workOrder.id]: workOrder };
      }, {});

      setWorkOrdersDictionary(dictionary);
      setLoading(false);
    };

    getWorkOrders();
  }, [calendarFilters.calendarStart, calendarFilters.calendarEnd]);

  const events = R.keys(workOrdersDictionary).map((workOrderId: string) => ({
    start: new Date(workOrdersDictionary[workOrderId].dueDate),
    end: new Date(workOrdersDictionary[workOrderId].dueDate),
    allDay: true,
    // Due to library constraints with passing props per event,
    // title is equal to the workOrder's id.
    title: workOrdersDictionary[workOrderId].id,
  }));

  const onSelectEvent = event => {
    navigate(`/workOrders/${event.title}`);
  };

  const getEventStyles = event => {
    const workOrder = workOrdersDictionary[event.title];
    const backgroundColor = getWorkOrderStatusCalendarColor(workOrder.statusId);

    return {
      style: {
        ...styles.event,
        backgroundColor,
      },
    };
  };

  return (
    <div className={clsx(classes.pane, classes.right)}>
      <Calendar
        localizer={localizer}
        defaultDate={viewDate}
        defaultView={view}
        events={events}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={onSelectEvent}
        style={styles.calendar}
        components={{
          toolbar: props => (
            <ScheduleCalendarToolbar
              {...props}
              setViewDate={setViewDate}
              setView={setView}
              loading={loading}
            />
          ),
          event: props => (
            <ScheduleCalendarEvent
              {...props}
              workOrdersDictionary={workOrdersDictionary}
            />
          ),
        }}
        eventPropGetter={getEventStyles}
      />
    </div>
  );
};

export default ScheduleCalendar;
